import { useEffect, useState } from "react";
import { Download, RefreshCw, Trash2, AlertTriangle, FileText } from "lucide-react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

/**
 * FTEXT Outbox viewer & downloader.
 *
 * Useful when the backend runs in a cloud container but the FTEXT driver lives
 * on a Windows machine: the operator downloads the generated .txt commands
 * here and drops them into C:\FText manually to test the syntax against the
 * real fiscal device.
 */
function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString("el-GR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function FTextOutbox() {
  const [data, setData] = useState({ dir: "", files: [], fallback_active: false, configured_folder: "", platform: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/ftext/outbox");
      setData(r.data);
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης outbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const downloadOne = async (name) => {
    try {
      const r = await http.get(`/ftext/outbox/${encodeURIComponent(name)}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Κατέβηκε ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα κατεβάσματος");
    }
  };

  const deleteOne = async (name) => {
    if (!(await confirmDialog(`Διαγραφή ${name};`))) return;
    try {
      await http.delete(`/ftext/outbox/${encodeURIComponent(name)}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής");
    }
  };

  const clearAll = async () => {
    if (!(await confirmDialog(`Διαγραφή ΟΛΩΝ των ${data.files.length} αρχείων;`))) return;
    try {
      const r = await http.delete("/ftext/outbox");
      toast.success(`Διαγράφηκαν ${r.data.deleted} αρχεία`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  return (
    <Card className="p-6 mt-6 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            FTEXT Outbox — Παραγόμενα Αρχεία
          </h3>
          <div className="text-xs text-slate-500 mt-1">
            Τα τελευταία <code>.txt</code> αρχεία που έχει γράψει το backend. Κατέβασέ τα και άσε
            τα στο <code>{data.configured_folder || "C:\\FText"}</code> της ταμειακής σου για να
            επαληθεύσεις τη σύνταξη των εντολών.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="ftext-outbox-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          {data.files.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              className="text-red-600 border-red-200"
              data-testid="ftext-outbox-clear-all"
            >
              <Trash2 size={14} className="mr-1" />
              Διαγραφή όλων
            </Button>
          )}
        </div>
      </div>

      {data.fallback_active && (
        <div
          className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded text-sm"
          data-testid="ftext-fallback-warning"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-900">
                Backend τρέχει σε {data.platform} αλλά έχεις δηλώσει Windows path
                <code className="ml-1">{data.configured_folder}</code>
              </div>
              <div className="text-amber-800 text-xs mt-1">
                Τα αρχεία γράφονται προσωρινά στο <code>{data.dir}</code> του cloud container —
                κατέβασέ τα από εδώ και αντέγραψέ τα στο <code>{data.configured_folder}</code> του
                Windows μηχανήματος όπου τρέχει ο FTEXT driver, για να δοκιμάσεις τις εντολές.
              </div>
            </div>
          </div>
        </div>
      )}

      {data.files.length === 0 ? (
        <div className="text-center text-slate-400 py-8 border-2 border-dashed border-slate-200 rounded-lg">
          Δεν υπάρχουν αρχεία ακόμη. Κάνε μια ενέργεια FTEXT (π.χ. έκδοση απόδειξης) για να
          εμφανιστούν.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Αρχείο</th>
                <th className="px-3 py-2 text-right w-24">Μέγεθος</th>
                <th className="px-3 py-2 text-left w-44">Χρόνος</th>
                <th className="px-3 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {data.files.map((f) => (
                <tr
                  key={f.name}
                  className="border-t border-slate-100 hover:bg-blue-50/30"
                  data-testid={`ftext-outbox-row-${f.name}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{f.name}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-slate-600">
                    {fmtBytes(f.size)}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtTime(f.mtime)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-blue-600 border-blue-200"
                        onClick={() => downloadOne(f.name)}
                        data-testid={`ftext-outbox-dl-${f.name}`}
                      >
                        <Download size={12} className="mr-1" />
                        DL
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-600"
                        onClick={() => deleteOne(f.name)}
                        data-testid={`ftext-outbox-del-${f.name}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
